export interface Decision {
  id: string;
  title: string;
  description: string;
  probability: number;
  children: Decision[];
}

export interface DecisionTree {
  question: string;
  decisions: Decision[];
}

// 将AI响应转换为结构化的决策树
export function parseAIResponseToDecisionTree(response: string): DecisionTree {
  try {
    console.log("原始响应:", response); // 调试日志
    
    // 尝试解析JSON响应
    const jsonResponse = JSON.parse(response);
    if (jsonResponse.decisions && Array.isArray(jsonResponse.decisions)) {
      return {
        question: jsonResponse.question || '',
        decisions: jsonResponse.decisions.map((d: any, index: number) => ({
          id: `d-${index}`,
          title: d.title,
          probability: d.probability,
          description: d.description || '',
          children: []
        }))
      };
    }
  } catch (e) {
    console.log("JSON解析失败，使用正则表达式解析", e); // 调试日志
    
    // 如果不是JSON格式，尝试使用正则表达式解析
    const decisions: Decision[] = [];
    
    // 先尝试匹配OpenAI格式的响应（**选项 X: [标题]** (成功概率: XX%)）
    const openaiDecisionRegex = /\*\*选项\s*\d+\s*:\s*([^*]+)\*\*\s*\(成功概率:\s*(\d+)%\)/g;
    const simpleDescriptionRegex = /\n([^*\n]+)(?=\n\*\*|\n$)/g;
    
    let index = 0;
    let match;
    
    // 重置正则表达式的lastIndex
    openaiDecisionRegex.lastIndex = 0;
    simpleDescriptionRegex.lastIndex = 0;
    
    let matchFound = false;
    
    // 测试OpenAI格式
    while ((match = openaiDecisionRegex.exec(response)) !== null) {
      matchFound = true;
      const title = match[1].trim();
      const probability = parseInt(match[2] || '0');
      
      // 获取描述文本（位于标题之后，下一个标题之前）
      const startPos = match.index + match[0].length;
      const endPos = response.indexOf('**选项', startPos);
      const descriptionText = endPos > -1 
        ? response.substring(startPos, endPos) 
        : response.substring(startPos);
      
      decisions.push({
        id: `d-${index++}`,
        title,
        probability,
        description: descriptionText.trim(),
        children: []
      });
    }
    
    // 如果没有匹配到OpenAI格式，尝试中文格式
    if (!matchFound) {
      // 中文格式的正则表达式
      const chineseDecisionRegex = /(?:选项|决策|方案)\s*[：:]\s*([^：:\n]+)(?:\s*[（(]成功率\s*[：:]\s*(\d+)%[）)]|\s*[（(](\d+)%[）)])/g;
      const chineseDescriptionRegex = /(?:分析|说明|描述)\s*[：:]\s*([^\n]+)/g;
      
      chineseDecisionRegex.lastIndex = 0;
      chineseDescriptionRegex.lastIndex = 0;
      
      let lastIndex = 0;
      
      while ((match = chineseDecisionRegex.exec(response)) !== null) {
        matchFound = true;
        const title = match[1].trim();
        const probability = parseInt(match[2] || match[3] || '0');
        
        // 查找该决策的描述
        let description = '';
        // 获取从上次匹配结束位置到当前匹配结束位置之间的文本
        const segmentToSearch = response.slice(lastIndex, match.index + match[0].length + 200); // 增加搜索范围
        chineseDescriptionRegex.lastIndex = 0; // 重置
        const descMatch = chineseDescriptionRegex.exec(segmentToSearch);
        if (descMatch) {
          description = descMatch[1].trim();
        }
        
        decisions.push({
          id: `d-${index++}`,
          title,
          probability,
          description,
          children: []
        });
        
        lastIndex = match.index + match[0].length;
      }
    }
    
    // 如果仍然没有找到决策，尝试使用更宽松的正则表达式
    if (!matchFound) {
      console.log("尝试使用宽松正则表达式解析"); // 调试日志
      
      // 分析文本，查找可能的决策选项
      // 先按行分割
      const lines = response.split('\n').filter(line => line.trim() !== '');
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // 尝试匹配"**任何内容**"格式或包含"选项"/"方案"的行
        if ((line.startsWith('**') && line.includes('**', 2)) || 
            line.includes('选项') || 
            line.includes('方案') || 
            line.includes('决策')) {
          
          // 尝试提取概率
          let probability = 0;
          const probMatch = line.match(/(\d+)%/);
          if (probMatch) {
            probability = parseInt(probMatch[1]);
          }
          
          // 提取标题（去除星号和括号等）
          let title = line
            .replace(/\*\*/g, '')
            .replace(/\(.*?\)/g, '')
            .replace(/（.*?）/g, '')
            .replace(/选项\s*\d+\s*[:：]/g, '')
            .replace(/方案\s*\d+\s*[:：]/g, '')
            .replace(/决策\s*\d+\s*[:：]/g, '')
            .trim();
          
          // 提取描述（使用下一行作为描述，如果存在）
          let description = '';
          if (i + 1 < lines.length) {
            description = lines[i + 1].trim();
            // 跳过已使用的描述行
            i++;
          }
          
          // 如果标题为空，继续下一个
          if (!title) continue;
          
          decisions.push({
            id: `d-${index++}`,
            title,
            probability,
            description,
            children: []
          });
        }
      }
    }
    
    if (decisions.length > 0) {
      console.log("解析成功，找到决策数:", decisions.length); // 调试日志
      return {
        question: response.split('\n')[0].trim(),
        decisions
      };
    } else {
      console.error("未能找到任何决策选项"); // 调试日志
    }
  }
  
  throw new Error('无法解析AI响应，请确保返回格式正确');
}

// 生成AI提示词
export const generateDecisionPrompt = (): string => {
  return `你是一个专业的决策分析助手。请根据用户的问题，分析不同的可能决策选项，并为每个选项评估成功概率。

严格遵循以下格式来响应，不要更改格式：

**选项 1: [决策标题]** (成功概率: XX%)
[详细描述选项1的利弊和原因]

**选项 2: [决策标题]** (成功概率: XX%)
[详细描述选项2的利弊和原因]

**选项 3: [决策标题]** (成功概率: XX%)
[详细描述选项3的利弊和原因]

(以此类推...)

格式要求:
1. 每个选项都必须以"**选项 序号: 标题**"开头，紧跟"(成功概率: XX%)"
2. 选项标题不要包含星号或其他特殊符号
3. 每个选项必须分配一个0-100之间的成功概率百分比
4. 每个选项标题后必须提供详细分析，描述该选项的利弊
5. 所有内容必须使用中文回答
6. 每个选项之间用空行分隔
7. 不要改变这个格式，这对系统正常工作至关重要

如果用户要求进一步分析某个特定选项，请遵循同样的格式，提供关于该选项的子选项，格式如下：

**选项 1: [子选项标题]** (成功概率: XX%)
[详细描述子选项1的利弊和原因]

**选项 2: [子选项标题]** (成功概率: XX%)
[详细描述子选项2的利弊和原因]

(以此类推...)`;
}; 